/**
 * THE BAR BETWEEN THE RAIL AND THE BOARD (Tim, 8 September: "in live/mock/farm add a handle
 * between the player lists and the boards to drag the left rail between 1x and 1.75x its current
 * width").
 *
 * The rail is 300px because a picklist row is a name, a chip and three controls and that is what
 * they cost. But what a man wants from it changes by the hour: reading the ranking for twenty
 * minutes between his picks, he wants the school and the bye and the whole surname; on the clock
 * he wants the board. `railtoggle` already answers the second — all of it or none of it — and
 * this answers the first, which is the question in between.
 *
 * ONE SETTING FOR ALL THREE BOARDS. Live, mock and the farm rounds are the same rail doing the
 * same job, and a width set on one that reverted on the next is a setting you stop using. It
 * survives a reload for the same reason.
 *
 * 1.75x IS THE STOP, not a free drag: past about 525px the board loses a column of cells, and a
 * rail wide enough to hide a round is a rail that has stopped being a rail. The lower stop is the
 * width the rows were drawn for.
 */

import { useCallback, useEffect, useState, type PointerEvent as ReactPointerEvent } from 'react';

const RAIL_KEY = 'ffl.railw.v1';
/** What the rows were drawn for — `.app.draftpg .draftcols` in the stylesheet. */
export const RAIL_1X = 300;
export const RAIL_MAX = Math.round(RAIL_1X * 1.75);
/** An arrow key moves it by a chip's width; the bar is reachable without a mouse. */
const STEP = 20;

export const clampRail = (n: number): number => Math.max(RAIL_1X, Math.min(RAIL_MAX, Math.round(n)));

/**
 * The width, remembered. A single hook, called once high up and passed down, because the two
 * boards that draw a rail are never on the screen together and must not disagree when you cross
 * between them.
 */
export function useRailWidth(): [number, (n: number) => void] {
  const [w, setW] = useState<number>(() => {
    try {
      const raw = Number(localStorage.getItem(RAIL_KEY));
      return Number.isFinite(raw) && raw > 0 ? clampRail(raw) : RAIL_1X;
    } catch { return RAIL_1X; }
  });
  const set = useCallback((n: number) => {
    const next = clampRail(n);
    setW(next);
    try { localStorage.setItem(RAIL_KEY, String(next)); } catch { /* private mode */ }
  }, []);
  return [w, set];
}

interface Props {
  w: number;
  onW: (n: number) => void;
}

export function RailGrip({ w, onW }: Props) {
  const [dragging, setDragging] = useState(false);

  /*
   * The cursor belongs to the WINDOW while a drag is on, not to the bar. A pointer moving faster
   * than React re-renders leaves the 10px track for a moment, and without this the cursor flickers
   * back to an arrow over whatever it is crossing and the drag reads as broken.
   */
  useEffect(() => {
    if (!dragging) return undefined;
    document.body.classList.add('railsizing');
    return () => document.body.classList.remove('railsizing');
  }, [dragging]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = w;
    setDragging(true);
    const move = (ev: PointerEvent) => {
      ev.preventDefault();
      onW(w0 + (ev.clientX - x0));
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const by = { ArrowLeft: -STEP, ArrowRight: STEP }[e.key];
    if (by !== undefined) { e.preventDefault(); onW(w + by); return; }
    if (e.key === 'Home') { e.preventDefault(); onW(RAIL_1X); }
    if (e.key === 'End') { e.preventDefault(); onW(RAIL_MAX); }
  };

  return (
    <div
      className={`railgrip${dragging ? ' sizing' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label="Width of the rail"
      aria-valuenow={w}
      aria-valuemin={RAIL_1X}
      aria-valuemax={RAIL_MAX}
      tabIndex={0}
      /* Back to the width the rows were drawn for, without aiming. */
      title="Drag to widen the rail · double-click to put it back"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => onW(RAIL_1X)}
    >
      <i aria-hidden="true" />
    </div>
  );
}
